import { useEffect } from "react";
import { Form } from "react-bootstrap";
import { Controller, useFormContext } from "react-hook-form";
import styled from "styled-components";
import ReactSelect from "react-select";

import { SelectChevronDown } from "../icons.jsx";
import { fontFamily } from "../grid.js";

export const CompactFieldLabel = styled("label")`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex: 1;
  height: 35px;
  padding: 0;
  margin: 0;
  color: #002b59;
  font:
    700 12px/12px ${fontFamily},
    "Segoe UI",
    sans-serif;

  &.disabled {
    opacity: 0.5;
    color: #999999;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

const SelectWrapper = styled("div").attrs({
  className: "CompactSelectWrapper",
})`
  .react-select__control {
    color: #111111;
    background-color: transparent;
    border: 0;
    padding: 0;
    min-height: auto;
    min-width: 150px;
    height: 35px;
    box-shadow: none;

    font:
      400 12px/12px ${fontFamily},
      "Segoe UI",
      sans-serif;
  }

  .react-select__control--is-disabled {
    opacity: 0.5;
    cursor: not-allowed;
    color: #999999;
    pointer-events: none;
  }

  .react-select__menu {
    z-index: 10;
  }
`;

export const FormCompactSelect = ({
  name,
  label,
  options,
  formGroupProps,
  selectProps,
  required,
  disabled,
  customErrorMessage,
  additionalClassnames,
  Icon,
  isCauseCodes,
}) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: required }}
      render={({
        field: { value, onChange, disabled: globalDisabled, onBlur, ref },
        fieldState: { error, isDirty },
      }) => {
        const errorType = error?.type;

        useEffect(() => {
          if (value && typeof onChange === "function") {
            if (!isDirty) onChange({ value }); // manually trigger the onChange
          }
        }, [value, onChange]);

        const helperText = () => {
          if (errorType === "required") {
            return "The field is required";
          }
          return errorType && customErrorMessage?.[errorType] ? customErrorMessage[errorType] : error?.message;
        };

        let selectedOption = "";
        if (options.length && value?.value) {
          // event.causeCode
          if (isCauseCodes && value.value.endsWith("P")) {
            // paid
            selectedOption = options[0].options.find((opt) => opt.value === value.value);
          } else if (isCauseCodes && value.value.endsWith("U")) {
            // unpaid
            selectedOption = options[1].options.find((opt) => opt.value === value.value);
          } else {
            // others
            selectedOption = options.find((opt) => opt.value === value.value);
          }
        }

        return (
          <Form.Group
            required={required}
            className={`d-flex flex-row gap-1 align-items-center ${additionalClassnames ?? ""} ${
              disabled || globalDisabled ? "disabled" : ""
            }`}
            controlId={`select-${name}`}
            style={{
              opacity: disabled || globalDisabled ? 0.5 : 1,
              pointerEvents: disabled || globalDisabled ? "none" : "auto",
              cursor: disabled || globalDisabled ? "not-allowed" : "auto",
            }}
            {...formGroupProps}
          >
            {label && (
              <CompactFieldLabel
                htmlFor={`select-${name}`}
                className={disabled || globalDisabled ? "disabled" : ""}
              >
                {Icon && <Icon />}
                {`${label}: ${required ? " *" : ""}`}
              </CompactFieldLabel>
            )}
            <SelectWrapper>
              <ReactSelect
                inputId={`select-${name}`}
                value={selectedOption}
                onChange={onChange}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
                defaultOptions
                ref={ref}
                disabled={disabled || globalDisabled}
                placeholder=""
                className="react-select-container"
                classNamePrefix="react-select"
                options={options}
                onBlur={onBlur}
                components={{
                  DropdownIndicator: SelectChevronDown,
                  IndicatorSeparator: null,
                  MultiValueRemove: null,
                }}
                {...selectProps}
              />
            </SelectWrapper>
          </Form.Group>
        );
      }}
    />
  );
};
